/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AdcModel, StripeProductModel } from '../../../../shared/models';
import { BaseComponent } from '../../../../../../core/components';
import { BillingApiService } from '../../../../shared/http';
import { CheckoutService } from '../../../../shared/services/component';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import {
    LogService,
    ModalService,
    NotifyService,
    RbacService,
    SocketService,
    StripeService,
    TabService,
    UtilsService
} from '../../../../../../core/services';
import { PortalRbacService } from '../../../../shared/services/utils';
import { Router } from '@angular/router';
import { environment } from '../../../../../../../environments/environment';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare let Intercom: any;

@Component({
    selector: 'app-overview',
    templateUrl: './billing-overview.component.html',
    styleUrls: ['./billing-overview.component.scss']
})
export class BillingOverviewComponent extends BaseComponent {
    /**
     * Billing table rows.
     */
    public billingRows: any[] = [];

    /**
     * Payment method table rows.
     */
    public methodRows: any[] = [];

    /**
     * History table rows.
     */
    public historyRows: any[] = [];

    /**
     * Set in environment file.
     */
    public stripeDisabled: boolean = true;

    /**
     * Store permissions in lookup table.
     */
    public permissionsLookup: any = {};

    constructor(
        public modalService: ModalService,
        private billingApiService: BillingApiService,
        private checkoutService: CheckoutService,
        private logService: LogService,
        private portalRbacService: PortalRbacService,
        private notifyService: NotifyService,
        private rbacService: RbacService,
        private router: Router,
        private socketService: SocketService,
        private stripeService: StripeService,
        private tabService: TabService,
        private utilsService: UtilsService
    ) {
        super();

        const state: any = this.router.getCurrentNavigation()?.extras?.state;
        if (state?.tabFilter) {
            this.tabService.activeTabId = state.tabFilter;
        }

        this.setUpStripe();
        this.setUpPageData(state);
        this.setupSocketListeners();
        this.registerButtons();
    }

    /**
     * CTA to open intercom chat.
     */
    public contactSales(message: string): void {
        Intercom('showNewMessage', message);
    }

    // /**
    //  * Make card default.
    //  * @param row
    //  */
    // public makeCardDefault(row: any): void {
    //
    // }

    /**
     * Download invoice.
     * @param row
     */
    public downloadInvoice(row: any): void {
        window.open(row.download, '_self');
    }

    /**
     * Confirm remove adc.
     * @param row
     */
    public confirmCancelSubscription(row: any): void {
        const confirmationObject = {
            title: 'billing_remove_subscription',
            message: 'billing_remove_subscription_message',
            callBack: this.cancelPlan.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Confirm remove adc.
     * @param row
     */
    public confirmRemovePaymentMethod(row: AdcModel): void {
        const confirmationObject = {
            title: 'billing_remove_payment',
            message: 'billing_remove_payment_message',
            callBack: this.removePaymentMethod.bind(this, row),
            options: { translateText: true }
        };

        this.modalService.confirmDecision(confirmationObject);
    }

    /**
     * Cancel plan
     * @param row
     * @private
     */
    private cancelPlan(row: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.billingApiService.deleteSubscription(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        if (row.activePortalSubscription) {
                            this.portalRbacService.selectedTier = 'free';
                        }

                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BillingOverviewComponent:cancelPlan', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Remove payment method.
     * @param row
     * @private
     */
    private removePaymentMethod(row: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            this.billingApiService.deletePaymentMethods(row.id)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.setUpPageData();
                        observer.next();
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('BillingOverviewComponent:removePaymentMethod', error.message);
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Set up stripe.
     * @private
     */
    private setUpStripe(): void {
        this.stripeDisabled = environment.stripeDisabled;
        if (this.stripeDisabled) {
            const timerId = setTimeout((): void => {
                this.notifyService.debug('Stripe disabled');
                clearTimeout(timerId);
            }, 500);
        }
    }

    /**
     * Set up page data.
     * @param state
     * @private
     */
    private setUpPageData(state?: any): void {
        const apiCalls: any = {};

        if (!this.stripeDisabled) {
            apiCalls['subscriptions'] = this.billingApiService.getSubscriptions();
            apiCalls['paymentMethods'] = this.billingApiService.getPaymentMethods();
            apiCalls['invoices'] = this.billingApiService.getInvoices();
            apiCalls['products'] = this.billingApiService.getAllProducts();
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.populateBillingTableRows(dataGroups.subscriptions, dataGroups.products);
                    this.populatePaymentMethodsTableRows(dataGroups.paymentMethods);
                    this.populateInvoicesTableRows(dataGroups.invoices);
                    this.messagingBasedOnState(state);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('BillingOverviewComponent:setUpPageData', error.message);
                }
            });
    }

    /**
     * Populate billing table rows.
     * @param subscriptions
     * @param products
     * @private
     */
    private populateBillingTableRows(subscriptions: any, products: StripeProductModel[]): void {
        const billing: any[] = [];
        const tierSubscriptions: any = this.utilsService.sortArrayByProperty(products, 'product_type', ['portal', 'adc_pack']);
        for (const subscription of subscriptions) {
            billing.push({
                id: subscription.id,
                item: subscription.stripePaymentID,
                description: this.getProductDescriptions(subscription.items, tierSubscriptions),
                status: this.utilsService.firstLetterUppercase(subscription.status),
                cost: this.getMonthlyCosts(subscription.items, tierSubscriptions),
                activePortalSubscription: subscription.status === 'active'
                    ? this.findActivePortalSubscriptionFromItems(subscription.items, products)
                    : false
            });
        }

        this.billingRows = this.utilsService.sortArrayByProperty(billing, 'status', ['Active', 'Pending', 'Canceled']);
    }

    /**
     * Get description of product(s).
     * @param items
     * @param tierSubscriptions
     * @private
     */
    private getProductDescriptions(items: any, tierSubscriptions: any[]): string {
        const description: any[] = [];
        for (const item of items) {
            const product = tierSubscriptions.find(product => product.stripe_product_id === item.stripeProductID);
            if (product?.description) {
                description.push(product.description);
            }

        }

        return description.length ? this.utilsService.replaceLastCommaWithAnd(description.toString(), true) : 'n/a';
    }

    /**
     * Get monthly costs.
     * @param items
     * @param tierSubscriptions
     * @private
     */
    private getMonthlyCosts(items: any, tierSubscriptions: any[]): string {
        let currency: string = '';
        let costs: number = 0;
        for (const item of items) {
            const product: any = tierSubscriptions.find((product: any): boolean => product.stripe_product_id === item.stripeProductID);
            if (product?.prices) {
                const monthlyProduct = product.prices.find((item: any): boolean => item.interval === 'month');
                costs = costs + (this.stripeService.getUnitPrice(monthlyProduct.unitAmount) * item.quantity);

                if (!currency.length) {
                    currency = this.stripeService.getCurrencySymbol(monthlyProduct.currency);
                }
            }
        }

        return currency + costs;
    }

    /**
     * Find active portal subscription.
     * @param items
     * @param products
     * @private
     */
    private findActivePortalSubscriptionFromItems(items: any[], products: StripeProductModel[]): boolean {
        const productIds: string[] = items.map(item => item.stripeProductID);
        const matchedProducts: StripeProductModel[] = products.filter(
            (item: StripeProductModel): boolean => productIds.includes(item.stripe_product_id)
        );

        return matchedProducts.some((item: StripeProductModel): boolean => item.product_type === 'portal');
    }

    /**
     * Populate payment method table rows.
     * @param paymentMethods
     * @private
     */
    private populatePaymentMethodsTableRows(paymentMethods: any): void {
        const tableRows: any = [];
        for (const method in paymentMethods) {
            if (paymentMethods.hasOwnProperty(method)) {
                const row: any = paymentMethods[method];
                // The expiry month is a month out from what is entered on the stripe form.
                const dateObject = new Date(row.expire_date);
                const adjustedDate = new Date(dateObject.setMonth(dateObject.getMonth() + 1));
                const date: string = this.utilsService.createDateTimeString(adjustedDate, false);
                tableRows.push({
                    id: row.id,
                    brand: row.brand,
                    brandLogoPath: this.getBrandLogo(row.brand),
                    expiry: date,
                    last4: row.last4,
                    expired: adjustedDate < new Date()
                });
            }
        }

        if (!tableRows?.length) {
            this.checkoutService.emptyCartItems();
        }

        this.methodRows = this.utilsService.sortArrayByProperty(tableRows, 'expired');
    }

    /**
     * Get logo path.
     * @param brand
     * @private
     */
    private getBrandLogo(brand: string): string {
        const knownBrands: string[] = [
            'amazon',
            'amex',
            'apple_pay',
            'bitcoin',
            'diners',
            'discover',
            'google_pay',
            'jcb',
            'maestro',
            'mastercard',
            'paypal',
            'visa',
        ];

        const name: string = knownBrands.includes(brand) ? brand : 'default_card';

        return `assets/centralisedManagement/icons/billing_${name}.svg`;
    }

    /**
     * Populate invoice table rows.
     * @param invoices
     * @private
     */
    private populateInvoicesTableRows(invoices: any): void {
        const tableRows: any = [];
        for (const invoice in invoices) {
            if (invoices.hasOwnProperty(invoice)) {
                const row: any = invoices[invoice];
                const amount: number = this.stripeService.getUnitPrice(row.total);
                tableRows.push({
                    id: row.id,
                    invoice: row.invoice_number,
                    date: this.utilsService.createDateTimeString(row.start),
                    amount,
                    status: this.getNotesText(row.status),
                    download: row.invoice_pdf
                });
            }
        }

        this.historyRows = tableRows;
    }

    /**
     * Get note text.
     * @param status
     * @private
     */
    private getNotesText(status: string): string {
        switch (status) {
            case 'draft':
                return 'draft';
            case 'open':
                return 'billing_waiting_payment';
            case 'paid':
                return 'billing_paid';
            case 'void':
                return 'void';
            case 'uncollectible':
                return 'billing_bad_debt';
            default:
                return 'no_status';
        }
    }

    /**
     * Message based on state.
     * @param state
     * @private
     */
    private messagingBasedOnState(state: any): void {
        if (state?.cancelSubscription) {
            this.modalService.openModalInParent('cancelSubscription');
        }
    }

    /**
     * Setup socket listeners.
     * @private
     */
    private setupSocketListeners(): void {
        this.socketService.on('subscriptionUpdate')
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (!this.billingRows.find((item: any): boolean => item.id === response.data?.id)) {
                        this.logService.error(
                            'BillingOverviewComponent:setupSocketListeners',
                            'Unable to match a subscription to update'
                        );
                    } else {
                        this.setUpPageData();
                    }
                }
            });
    }

    /**
     * Register the page's buttons for RBAC.
     * @private
     */
    private registerButtons(): void {
        const buttonReference = {
            'billing-cancel-subscription': [{ path: '/api/v1/billing/subscriptions/:id', methods: ['DELETE'] }],
            'billing-edit-payment_method': [{ path: '/api/v1/billing/payment-methods/:pm-id', methods: ['GET'] },],
            'billing-delete-payment_method': [{ path: '/api/v1/billing/payment-methods/:pm-id', methods: ['DELETE'] }],
            'billing-add-payment_method': [{ path: '/api/v1/billing/payment-methods', methods: ['POST'] }],
        };

        this.permissionsLookup = this.rbacService.getPermissionsLookup(buttonReference);
    }
}
